import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Puzzle, Search } from "lucide-react";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
const rise = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 } };

const CAT_COLOR = {
  Payments: "#18c9ff", Comms: "#00f0ff", Productivity: "#a855f7", Data: "#3b82f6",
  Finance: "#ffb800", DevOps: "#70e4ff", AI: "#8b5cf6", Storage: "#6366f1", Automation: "#ef4444",
};

export default function PluginsPage() {
  const [plugins, setPlugins] = useState([]);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");

  const load = () => apiGet("/plugins").then(setPlugins).catch(() => {});
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(plugins.map((p) => p.category)))], [plugins]);
  const filtered = plugins.filter(
    (p) =>
      (cat === "All" || p.category === cat) &&
      (p.name.toLowerCase().includes(query.toLowerCase()) || p.desc.toLowerCase().includes(query.toLowerCase()))
  );
  const configuredCount = plugins.filter((p) => p.configured).length;

  return (
    <div className="h-full overflow-y-auto p-5 lg:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Puzzle className="w-6 h-6 text-jarvis-purple" />
          <div>
            <h1 className="font-mono text-xl lg:text-2xl uppercase tracking-[0.25em] font-bold text-jarvis-purple drop-shadow-[0_0_12px_rgba(168,85,247,0.4)]">
              Plugins
            </h1>
            <p className="font-mono text-[11px] text-slate-500 tracking-widest">
              {configuredCount} key configured · {plugins.length} catalog entries
            </p>
          </div>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="plugin-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search plugins…"
            className="w-64 bg-black/40 border border-jarvis-purple/20 rounded-md pl-9 pr-3 py-2 text-sm font-code text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-jarvis-purple/50"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`plugin-cat-${c}`}
            className={cn(
              "font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-md border transition-all",
              cat === c
                ? "border-jarvis-purple/50 text-jarvis-violet bg-jarvis-purple/10"
                : "border-white/10 text-slate-500 hover:text-slate-300"
            )}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((p) => {
          const color = CAT_COLOR[p.category] || "#00f0ff";
          return (
            <motion.div
              key={p.id}
              layout
              {...rise}
              className="jv-panel p-4 flex flex-col"
              data-testid={`plugin-tile-${p.name}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-9 h-9 rounded-md flex items-center justify-center font-mono font-bold text-sm border"
                    style={{ color, borderColor: `${color}55`, background: `${color}12` }}
                  >
                    {p.name[0]}
                  </div>
                  <div>
                    <div className="font-mono text-sm font-bold text-slate-100">{p.name}</div>
                    <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color }}>{p.category}</span>
                  </div>
                </div>
                <span data-testid={`plugin-connection-${p.name}`} className="font-mono text-[9px] text-slate-500">{p.configured ? "Key configured" : "Not connected"}</span>
              </div>
              <p className="text-[12px] text-slate-400 leading-relaxed flex-1">{p.desc}</p>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                {p.configured ? "Manage and test credentials in Settings" : "Connector not configured"}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
